import React, { useState } from 'react';
import { Box, IconButton, Typography, useTheme, Menu, MenuItem, Divider } from '@mui/material';
import {
  ArrowBackIos as PreviousPageIcon,
  ArrowForwardIos as NextPageIcon,
  MenuBook as BookIcon,
  Home as HomeIcon,
  PhotoCamera as CameraIcon,
  Settings as SettingsIcon,
  HelpOutline as HelpIcon,
  Info as InfoIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../utils/constants';
import { getPageNumberForRoute } from '../utils/pageNumbers';
import { logOut } from '../services/authService';
import { useRecipes } from '../context/RecipeContext';

interface BookNavigationProps {
  currentPageNumber?: number;
  showPageIndicator?: boolean;
  viewMode?: 'cover' | 'toc' | 'grid';
  onTocNext?: () => void;
  onTocPrevious?: () => void;
  canTocNext?: boolean;
  canTocPrevious?: boolean;
}

const BookNavigation: React.FC<BookNavigationProps> = ({
  currentPageNumber,
  showPageIndicator = true,
  viewMode,
  onTocNext,
  onTocPrevious,
  canTocNext,
  canTocPrevious,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { recipes } = useRecipes();

  // Determine current page if not provided
  const currentPage = currentPageNumber || getPageNumberForRoute(location.pathname);

  // Define navigation structure based on book flow
  const getNavigationTargets = () => {
    const pathParts = location.pathname.split('/');
    
    // Check if we're on a recipe step page FIRST (more specific)
    if (pathParts[1] === 'recipes' && pathParts[2] && pathParts[3] === 'steps') {
      const currentRecipeId = pathParts[2];
      const currentStepIndex = parseInt(pathParts[4] || '0');
      const currentRecipeIndex = recipes.findIndex(r => r.id === currentRecipeId);
      const currentRecipe = recipes[currentRecipeIndex];
      
      if (currentRecipe && currentRecipe.steps) {
        const totalSteps = currentRecipe.steps.length;
        
        // Navigate through steps within the same recipe
        return {
          previous: currentStepIndex > 0
            ? { 
                route: `/recipes/${currentRecipeId}/steps/${currentStepIndex - 1}`, 
                label: `Step ${currentStepIndex}` 
              }
            : { 
                route: `/recipes/${currentRecipeId}`, 
                label: 'Recipe Details' 
              },
          next: currentStepIndex < totalSteps - 1
            ? { 
                route: `/recipes/${currentRecipeId}/steps/${currentStepIndex + 1}`, 
                label: `Step ${currentStepIndex + 2}` 
              }
            : { 
                route: `/recipes/${currentRecipeId}`, 
                label: 'Back to Recipe' 
              },
        };
      }
    }
    
    // Check if we're on a recipe detail page (less specific, check after steps)
    if (pathParts[1] === 'recipes' && pathParts[2] && pathParts[2] !== 'new') {
      const currentRecipeId = pathParts[2];
      const currentIndex = recipes.findIndex(r => r.id === currentRecipeId);
      
      if (currentIndex !== -1) {
        // Navigate through recipes sequentially
        const previousRecipe = currentIndex > 0 ? recipes[currentIndex - 1] : null;
        const nextRecipe = currentIndex < recipes.length - 1 ? recipes[currentIndex + 1] : null;
        
        return {
          previous: previousRecipe 
            ? { route: `/recipes/${previousRecipe.id}`, label: previousRecipe.title }
            : { route: ROUTES.RECIPES, label: 'Table of Contents' },
          next: nextRecipe
            ? { route: `/recipes/${nextRecipe.id}`, label: nextRecipe.title }
            : { route: ROUTES.RECIPE_CAPTURE, label: 'Add Recipe' },
        };
      }
    }
    
    // Default navigation for other pages
    switch (location.pathname) {
      case '/home':
      case '/':
        return {
          previous: null,
          next: recipes.length > 0
            ? { route: ROUTES.RECIPES, label: 'Table of Contents' }
            : { route: ROUTES.RECIPE_CAPTURE, label: 'Add Recipe' },
        };
      case '/recipes':
        // Handle TOC pagination if in TOC mode
        if (viewMode === 'toc') {
          const navTargets = {
            previous: canTocPrevious
              ? { route: null, label: 'Previous TOC Page', action: onTocPrevious }
              : { route: '/home', label: 'Title Page' },
            next: canTocNext
              ? { route: null, label: 'Next TOC Page', action: onTocNext }
              : recipes.length > 0
                ? { route: `/recipes/${recipes[0].id}`, label: recipes[0].title }
                : { route: ROUTES.RECIPE_CAPTURE, label: 'Add Recipe' },
          };
          console.log('TOC navigation targets:', navTargets);
          return navTargets;
        }
        return {
          previous: { route: '/home', label: 'Title Page' },
          next: recipes.length > 0
            ? { route: `/recipes/${recipes[0].id}`, label: recipes[0].title }
            : { route: ROUTES.RECIPE_CAPTURE, label: 'Add Recipe' },
        };
      case '/capture':
        return {
          previous: recipes.length > 0
            ? { route: `/recipes/${recipes[recipes.length - 1].id}`, label: recipes[recipes.length - 1].title }
            : { route: ROUTES.RECIPES, label: 'Table of Contents' },
          next: { route: ROUTES.SETTINGS, label: 'Settings' },
        };
      case '/settings':
        return {
          previous: { route: ROUTES.RECIPE_CAPTURE, label: 'Add Recipe' },
          next: null,
        };
      default:
        return {
          previous: { route: ROUTES.RECIPES, label: 'Table of Contents' },
          next: null,
        };
    }
  };

  const { previous, next } = getNavigationTargets();

  const handleNavigation = (target: { route: string | null; action?: () => void } | null) => {
    console.log('handleNavigation called with:', target);
    if (!target) {
      return;
    }

    // If there's an action, execute it instead of navigating
    if (target.action) {
      console.log('Executing action');
      target.action();
    } else if (target.route) {
      console.log('Navigating to:', target.route);
      navigate(target.route);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const handleLogout = async () => {
    try {
      await logOut();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Error signing out:', error);
    }
    handleMenuClose();
  };

  return (
    <>
      <Box
        sx={{
        position: { xs: 'fixed', md: 'absolute' },
        bottom: { xs: 10, md: 15 },
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, md: 3 },
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(46, 33, 23, 0.95)'
          : 'rgba(255, 248, 240, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: { xs: '16px', md: '20px' },
        padding: { xs: '8px 16px', md: '12px 20px' },
        border: `1px solid ${theme.palette.mode === 'dark'
          ? 'rgba(255, 138, 101, 0.2)'
          : 'rgba(139, 69, 19, 0.2)'}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.5)'
          : '0 8px 32px rgba(139, 69, 19, 0.2)',
        maxWidth: { xs: 'calc(100% - 20px)', md: 'auto' },
      }}
    >
      {/* Previous Page */}
      <IconButton
        onClick={() => handleNavigation(previous)}
        disabled={!previous}
        component={motion.button}
        whileHover={{ scale: previous ? 1.1 : 1 }}
        whileTap={{ scale: previous ? 0.9 : 1 }}
        size="small"
        sx={{
          p: { xs: 0.75, md: 1 },
          color: previous
            ? (theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.8)'
                : 'rgba(139, 69, 19, 0.8)')
            : (theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.3)'
                : 'rgba(139, 69, 19, 0.3)'),
          '&:hover': previous ? {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.1)'
              : 'rgba(139, 69, 19, 0.1)',
            color: theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.9)'
              : 'rgba(139, 69, 19, 0.9)',
          } : {},
        }}
        title={previous?.label}
      >
        <PreviousPageIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
      </IconButton>

      {/* Center Menu Button */}
      <IconButton
        onClick={handleMenuOpen}
        component={motion.button}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        size="small"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.3, md: 0.5 },
          p: { xs: 0.75, md: 1 },
          color: theme.palette.mode === 'dark'
            ? 'rgba(255, 138, 101, 0.8)'
            : 'rgba(139, 69, 19, 0.8)',
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.1)'
              : 'rgba(139, 69, 19, 0.1)',
          },
        }}
      >
        <BookIcon sx={{ fontSize: { xs: 20, md: 24 } }} />
        {showPageIndicator && (
          <Typography
            variant="body2"
            sx={{
              fontFamily: '"Crimson Text", serif',
              fontSize: { xs: '0.8rem', md: '0.9rem' },
              fontWeight: 500,
              ml: { xs: 0.3, md: 0.5 },
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {currentPage}
          </Typography>
        )}
      </IconButton>

      {/* Menu Dropdown */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(46, 33, 23, 0.98)'
              : '#FFFEF8',
            border: `1px solid ${theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.3)'
              : 'rgba(139, 69, 19, 0.3)'}`,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(139, 69, 19, 0.15)',
            minWidth: { xs: 200, md: 220 },
          }
        }}
      >
        <MenuItem
          onClick={() => handleMenuNavigation('/home')}
          sx={{ py: { xs: 1.5, md: 1 }, minHeight: { xs: 48, md: 'auto' } }}
        >
          <HomeIcon sx={{ mr: 2, fontSize: { xs: 22, md: 20 } }} />
          <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: { xs: '1rem', md: '0.95rem' } }}>
            Home
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuNavigation(ROUTES.RECIPES)}
          sx={{ py: { xs: 1.5, md: 1 }, minHeight: { xs: 48, md: 'auto' } }}
        >
          <BookIcon sx={{ mr: 2, fontSize: { xs: 22, md: 20 } }} />
          <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: { xs: '1rem', md: '0.95rem' } }}>
            Recipe Book
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuNavigation(ROUTES.RECIPE_CAPTURE)}
          sx={{ py: { xs: 1.5, md: 1 }, minHeight: { xs: 48, md: 'auto' } }}
        >
          <CameraIcon sx={{ mr: 2, fontSize: { xs: 22, md: 20 } }} />
          <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: { xs: '1rem', md: '0.95rem' } }}>
            Add Recipe
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuNavigation(ROUTES.SETTINGS)}
          sx={{ py: { xs: 1.5, md: 1 }, minHeight: { xs: 48, md: 'auto' } }}
        >
          <SettingsIcon sx={{ mr: 2, fontSize: { xs: 22, md: 20 } }} />
          <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: { xs: '1rem', md: '0.95rem' } }}>
            Settings
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={() => handleMenuNavigation(ROUTES.HELP)}
          sx={{ py: { xs: 1.5, md: 1 }, minHeight: { xs: 48, md: 'auto' } }}
        >
          <HelpIcon sx={{ mr: 2, fontSize: { xs: 22, md: 20 } }} />
          <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: { xs: '1rem', md: '0.95rem' } }}>
            Help
          </Typography>
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuNavigation(ROUTES.ABOUT)}
          sx={{ py: { xs: 1.5, md: 1 }, minHeight: { xs: 48, md: 'auto' } }}
        >
          <InfoIcon sx={{ mr: 2, fontSize: { xs: 22, md: 20 } }} />
          <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: { xs: '1rem', md: '0.95rem' } }}>
            About
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 1 }} />
        <MenuItem
          onClick={handleLogout}
          sx={{ py: { xs: 1.5, md: 1 }, minHeight: { xs: 48, md: 'auto' } }}
        >
          <LogoutIcon sx={{ mr: 2, fontSize: { xs: 22, md: 20 } }} />
          <Typography sx={{ fontFamily: '"Crimson Text", serif', fontSize: { xs: '1rem', md: '0.95rem' } }}>
            Sign Out
          </Typography>
        </MenuItem>
      </Menu>

      {/* Next Page */}
      <IconButton
        onClick={() => handleNavigation(next)}
        disabled={!next}
        component={motion.button}
        whileHover={{ scale: next ? 1.1 : 1 }}
        whileTap={{ scale: next ? 0.9 : 1 }}
        size="small"
        sx={{
          p: { xs: 0.75, md: 1 },
          color: next
            ? (theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.8)'
                : 'rgba(139, 69, 19, 0.8)')
            : (theme.palette.mode === 'dark'
                ? 'rgba(255, 248, 240, 0.3)'
                : 'rgba(139, 69, 19, 0.3)'),
          '&:hover': next ? {
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.1)'
              : 'rgba(139, 69, 19, 0.1)',
            color: theme.palette.mode === 'dark'
              ? 'rgba(255, 138, 101, 0.9)'
              : 'rgba(139, 69, 19, 0.9)',
          } : {},
        }}
        title={next?.label}
      >
        <NextPageIcon sx={{ fontSize: { xs: 18, md: 20 } }} />
      </IconButton>
      </Box>
    </>
  );
};

export default React.memo(BookNavigation);